import { AxiosRequestConfig } from "axios";
import { observe } from "mobx";
import { observer } from "mobx-react";
import React from "react";
import NotificationSystem from "react-notification-system";
import styled, { createGlobalStyle } from "styled-components";

import CopyMultilineValue from "@client/components/ui/CopyMultilineValue";
import NewModal from "@client/components/ui/Modal";
import notificationStore from "@client/stores/NotificationStore";
import DomUtils from "@client/utils/DomUtils";
import Style from "@client/utils/Style";
import { AlertInfo } from "@typings/NotificationItem";

import NotificationsStyle from "./style";
import Button from "@client/components/ui/Button";

const GlobalStyle = createGlobalStyle`
  ${NotificationsStyle}
`;

const Wrapper = styled.div<any>``;
const ErrorInfoWrapper = styled.div<any>`
  margin: 32px;
  overflow: auto;
`;
const ErrorInfoRequest = styled.div<any>``;
const ErrorInfoRequestItem = styled.div<any>`
  margin-bottom: 16px;
`;
const ErrorInfoRequestLabel = styled.div<any>`
  font-size: 10px;
  font-weight: ${Style.fontWeights.medium};
  color: ${Style.palette.grayscale[3]};
  text-transform: uppercase;
  margin-bottom: 4px;
`;
const ErrorInfoRequestData = styled.pre`
  word-break: break-word;
  white-space: pre-wrap;
  margin: 0;
  .key {
    color: #053997;
  }
  .number {
    color: #107947;
  }
  .string {
    color: #92000c;
  }
  .boolean {
    color: #0000ff;
  }
  .null {
    color: #000a5d;
  }
`;
const ButtonWrapper = styled.div<any>`
  display: flex;
  justify-content: center;
  margin-bottom: 32px;
`;

type State = {
  errorInfo: {
    error: { message: string | null; status: string | null };
    request: AxiosRequestConfig;
  } | null;
};

const MAX_NOTIFICATIONS = 3;

@observer
class NotificationsModule extends React.Component<
  Record<string, never>,
  State
> {
  state: State = {
    errorInfo: null,
  };

  notificationSystem!: NotificationSystem;

  notificationsCount = 0;

  activeNotifications: any[] = [];

  componentDidMount() {
    observe(notificationStore.alerts, change => {
      this.handleStoreChange(change.object);
    });
  }

  handleStoreChange(alerts: AlertInfo[]) {
    if (!alerts.length || alerts.length <= this.notificationsCount) {
      return;
    }
    const lastNotification = alerts[alerts.length - 1];
    let action = lastNotification.options
      ? lastNotification.options.action
      : undefined;
    let autoDismiss = lastNotification.message.length < 150 ? 10 : 30;
    if (action && lastNotification.level === "error") {
      const errorInfo = action.callback();
      action = {
        ...action,
        callback: () => {
          this.setState({ errorInfo });
        },
      };
      autoDismiss = 0;
    }

    this.notificationSystem.addNotification({
      title: lastNotification.title || lastNotification.message,
      message: lastNotification.title ? lastNotification.message : undefined,
      level: lastNotification.level || "info",
      position: "br",
      autoDismiss,
      action,
      onAdd: (notification: any) => {
        this.activeNotifications.push(notification);
        for (
          let i = 0;
          i < this.activeNotifications.length - MAX_NOTIFICATIONS;
          i += 1
        ) {
          this.notificationSystem.removeNotification(
            this.activeNotifications[i].uid,
          );
        }
      },
      onRemove: (notification: any) => {
        this.activeNotifications = this.activeNotifications.filter(
          n => n.uid !== notification.uid,
        );
      },
    });

    this.notificationsCount = alerts.length;
  }

  render() {
    const error = this.state.errorInfo;
    let jsonData = error && error.request.data;
    try {
      jsonData = JSON.stringify(jsonData, null, 2);
      jsonData = DomUtils.jsonSyntaxHighlight(jsonData);
      // eslint-disable-next-line no-empty
    } catch (err) {}
    return (
      <Wrapper>
        <GlobalStyle />
        <NotificationSystem
          ref={(n: NotificationSystem) => {
            this.notificationSystem = n;
          }}
        />
        {error ? (
          <NewModal
            title="Error Details"
            isOpen
            onRequestClose={() => {
              this.setState({ errorInfo: null });
            }}
          >
            <ErrorInfoWrapper>
              <ErrorInfoRequest>
                <ErrorInfoRequestItem>
                  <ErrorInfoRequestLabel>Request URL</ErrorInfoRequestLabel>
                  <CopyMultilineValue value={error.request.url} />
                </ErrorInfoRequestItem>
                <ErrorInfoRequestItem>
                  <ErrorInfoRequestLabel>Request Method</ErrorInfoRequestLabel>
                  <CopyMultilineValue value={error.request.method || "GET"} />
                </ErrorInfoRequestItem>
                {error.request.data ? (
                  <ErrorInfoRequestItem>
                    <ErrorInfoRequestLabel>Request Data</ErrorInfoRequestLabel>
                    <ErrorInfoRequestData
                      dangerouslySetInnerHTML={{ __html: jsonData }}
                    />
                  </ErrorInfoRequestItem>
                ) : null}
                <ErrorInfoRequestItem>
                  <ErrorInfoRequestLabel>Response Status</ErrorInfoRequestLabel>
                  <CopyMultilineValue value={error.error.status || "-"} />
                </ErrorInfoRequestItem>
                {error.error.message ? (
                  <ErrorInfoRequestItem>
                    <ErrorInfoRequestLabel>
                      Response Message
                    </ErrorInfoRequestLabel>
                    <CopyMultilineValue value={error.error.message} />
                  </ErrorInfoRequestItem>
                ) : null}
              </ErrorInfoRequest>
            </ErrorInfoWrapper>
            <ButtonWrapper>
              <Button
                secondary
                onClick={() => {
                  this.setState({ errorInfo: null });
                }}
              >
                Dismiss
              </Button>
            </ButtonWrapper>
          </NewModal>
        ) : null}
      </Wrapper>
    );
  }
}

export default NotificationsModule;
